import { useEffect, useRef } from 'react'
import { collectDestinations } from '../utils/collectDestinations'
import type {
  OperationsNode,
  OperationsScheduleNode,
} from '@sportninja/common/features/competition/operations/types/operations.types'

interface Options {
  nodes: OperationsNode[]
  isLoading: boolean
  /** Suppresses the hand-off — teams come from the Registration tab instead. */
  isRegistrationOn?: boolean
  onTrigger: (division: OperationsScheduleNode) => void
}

/**
 * Restores the competition wizard's `?openAddTeam=true` hand-off.
 *
 * The wizard redirects here after manual setup so the user is prompted to add
 * their first team. Fires at most once, only after the tree has loaded, then
 * strips the param so a refresh does not re-trigger it.
 *
 * Skipped entirely while registration is open: every Add Team button is hidden
 * in that state (see `canAddChild`), so springing the modal open from a stale
 * link would contradict the rest of the tab.
 */
export const useOpenAddTeamDeepLink = ({
  nodes,
  isLoading,
  isRegistrationOn = false,
  onTrigger,
}: Options): void => {
  const hasFired = useRef(false)

  useEffect(() => {
    if (hasFired.current || isLoading || nodes.length === 0) {
      return
    }

    if (isRegistrationOn) {
      return
    }

    const params = new URLSearchParams(window.location.search)
    if (params.get('openAddTeam') !== 'true') {
      return
    }

    const division = collectDestinations(nodes)[0] ?? null
    if (!division) {
      return
    }

    hasFired.current = true
    onTrigger(division)

    params.delete('openAddTeam')
    const query = params.toString()
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}`
    )
  }, [nodes, isLoading, isRegistrationOn, onTrigger])
}
